
import { TrainingSession } from '@/types/training';
import { STORAGE_KEYS } from '@/types/training';
import { TrainingFormValues } from '@/features/training-journal/types';

// Persistent fields that should carry over to new sessions
interface PersistentFields {
  sessionType?: string;
  duration?: number;
  professor?: string;
  location?: string;
}

export const loadSessions = (): TrainingSession[] => {
  try {
    const sessionsJson = localStorage.getItem(STORAGE_KEYS.SESSIONS);
    let sessions: TrainingSession[] = sessionsJson ? JSON.parse(sessionsJson) : [];
    
    // Ensure all sessions have required fields
    sessions = sessions.map(session => ({
      ...session,
      techniques: session.techniques || [],
      notes: session.notes || '',
      user_id: session.userId || 'anonymous',
      created_at: session.createdAt || new Date().toISOString(),
      updated_at: session.updatedAt || new Date().toISOString()
    }));
    
    return sessions;
  } catch (error) {
    console.error('Error loading sessions from localStorage:', error);
    return [];
  }
};

export const saveTimePeriod = (period: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.TIME_PERIOD, period);
  } catch (error) {
    console.error('Error saving time period to localStorage:', error);
  }
};

export const loadTimePeriod = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.TIME_PERIOD);
  } catch (error) {
    console.error('Error loading time period from localStorage:', error);
    return null;
  }
};

export const saveProfessor = (professor: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PROFESSOR, professor);
  } catch (error) {
    console.error('Error saving professor to localStorage:', error);
  }
};

export const loadLastUsedProfessor = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEYS.PROFESSOR) || '';
  } catch (error) {
    console.error('Error loading professor from localStorage:', error);
    return '';
  }
};

// New functions for persistent fields
export const savePersistentFields = (fields: PersistentFields): void => {
  try {
    localStorage.setItem('training-persistent-fields', JSON.stringify(fields));

  } catch (error) {
    console.error('Error saving persistent fields:', error);
  }
};

export const loadPersistentFields = (): PersistentFields => {
  try {
    const stored = localStorage.getItem('training-persistent-fields');
    if (stored) {
      const fields = JSON.parse(stored);

      return fields;
    }
    return {};
  } catch (error) {
    console.error('Error loading persistent fields:', error);
    return {};
  }
};

export const saveFormDraft = (
  formData: Partial<TrainingFormValues>
): void => {
  try {
    const draftData = {
      ...formData,
      date: formData.date instanceof Date ? formData.date.toISOString() : formData.date,
      timestamp: new Date().toISOString() // Add timestamp to track when draft was created
    };
    
    localStorage.setItem(STORAGE_KEYS.FORM_DRAFT, JSON.stringify(draftData));
  } catch (error) {
    console.error('Error saving form draft to localStorage:', error);
  }
};

export const loadFormDraft = (): Partial<TrainingFormValues> | null => {
  try {
    const draftJson = localStorage.getItem(STORAGE_KEYS.FORM_DRAFT);
    if (!draftJson) return null;

    const draft = JSON.parse(draftJson) as Partial<TrainingFormValues>;

    if (draft.date) {
      draft.date = new Date(draft.date as unknown as string);
    }
    
    return draft;
  } catch (error) {
    console.error('Error loading form draft from localStorage:', error);
    return null;
  }
};

export const clearFormDraft = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.FORM_DRAFT);

  } catch (error) {
    console.error("Error clearing form draft:", error);
  }
};

// Check if there's a meaningful draft (not just auto-saved empty form)
export const hasMeaningfulDraft = (): boolean => {
  try {
    const draft = loadFormDraft();
    if (!draft) return false;
    
    // Check if draft has meaningful content beyond just persistent fields
    const meaningfulFields = ['notes', 'learned', 'improvements', 'successes', 'techniques', 'youtube_url'];
    return meaningfulFields.some(field => {
      const value = draft[field as keyof TrainingFormValues];
      return value && value !== '' && (typeof value !== 'object' || (Array.isArray(value) && value.length > 0));
    });
  } catch (error) {
    console.error('Error checking meaningful draft:', error);
    return false;
  }
};
